import React, { useEffect, useState } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Box,
	Typography,
	useTheme
} from "@mui/material";
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import api from "../../services/api";

const KanbanTransferCardModal = ({
	cardId,
	columnId,
	open,
	onClose,
	onConfirm
}) => {

	const theme = useTheme();
	const [loading, setLoading] = useState({ kanbans: false, card: false, columns: false, transfer: false });
	const [kanbans, setKanbans] = useState([]);
	const [columns, setColumns] = useState([]);
	const [card, setCard] = useState(null);
	const [selectedKanban, setSelectedKanban] = useState('');
	const [selectedColumn, setSelectedColumn] = useState('');

	useEffect(() => {
		const fetchKanbans = async () => {
			if (!open) return;

			try {
				setLoading(prev => ({ ...prev, card: true }));
				const { data } = await api.get(`/kanban/card/${cardId}`);
				setCard(data);
				console.info({ data })
				// Set default kanban from card
				if (data?.kanbanId) {
					setSelectedKanban(data.kanbanId);
				}
			} catch (error) {
				console.error(error);
			} finally {
				setLoading(prev => ({ ...prev, card: false }));
			}
		};

		fetchKanbans();
	}, [open, cardId]);

	// Fetch available kanbans and set default kanban
	useEffect(() => {
		const fetchKanbans = async () => {
			if (!open) return;

			try {
				setLoading(prev => ({ ...prev, kanbans: true }));
				const { data } = await api.get('/kanbans/list');
				setKanbans(data);

				// Set default kanban from card
				if (card?.kanbanId) {
					setSelectedKanban(card.kanbanId);
				}
			} catch (error) {
				console.error(error);
			} finally {
				setLoading(prev => ({ ...prev, kanbans: false }));
			}
		};

		fetchKanbans();
	}, [open, card]);

	// Fetch columns when a kanban is selected
	useEffect(() => {
		const fetchColumns = async () => {
			if (!selectedKanban) return;

			try {
				setLoading(prev => ({ ...prev, columns: true }));
				const { data } = await api.get(`/kanban/${selectedKanban}/columns/list`);
				const findColumn = data.findIndex((column) => column.id === columnId);
				if (findColumn != -1) {
					setSelectedColumn(columnId);
				}
				setColumns(data);
			} catch (error) {
				console.error('Failed to fetch columns:', error);
			} finally {
				setLoading(prev => ({ ...prev, columns: false }));
			}
		};

		fetchColumns();
	}, [selectedKanban]);

	// Handle kanban selection
	const handleKanbanChange = (event) => {
		setSelectedKanban(event.target.value);
		setSelectedColumn(''); // Reset column when kanban changes
	};

	// Handle transfer confirmation
	const handleConfirm = async () => {
		if (selectedKanban && selectedColumn) {
			try {
				setLoading(prev => ({ ...prev, transfer: true }));
				await onConfirm({
					kanbanId: selectedKanban,
					columnId: selectedColumn,
					isChangeKanban: card?.kanbanId != selectedKanban,
					cardId: cardId
				});
			} catch (error) {
				console.error(error)
			} finally {
				setLoading(prev => ({ ...prev, transfer: false }));
				onClose();
			}
		}
	};

	return (
		<Dialog
			open={open}
			onClose={() => onClose(false)}
			maxWidth="sm"
			fullWidth
			BackdropProps={{
				sx: {
					backdropFilter: 'blur(5px)',
					backgroundColor: 'rgba(0, 0, 30, 0.3)'
				}
			}}
		>
			<DialogTitle
				sx={{
					padding: '15px',
					display: 'flex',
					alignItems: 'center',
					gap: 2,
					borderBottom: `1px solid ${theme.palette.divider}`
				}}
			>
				<SwapHorizIcon />
				Transferir Tarefa: {card?.title}
			</DialogTitle>

			<DialogContent
				dividers
				sx={{
					padding: theme.spacing(3)
				}}
			>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
					<Typography
						variant="body1"
						color="text.secondary"
						sx={{
							mb: 2,
							p: 2,
							borderRadius: 2,
							border: `1px dashed ${theme.palette.grey[300]}`
						}}
					>
						<WarningAmberIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
						Você pode transferir a tarefa para outros quadros ou colunas.
						Ao transferir para outro quadro, você <strong>perderá</strong> o vínculo com o quadro atual.
					</Typography>

					<FormControl
						fullWidth
						variant="outlined"
						size="small"
					>
						<InputLabel>Quadro</InputLabel>
						<Select
							value={selectedKanban}
							label="Quadro"
							onChange={handleKanbanChange}
							disabled={loading.kanbans}
						>
							{kanbans.map((kanban) => (
								<MenuItem
									key={kanban.id}
									value={kanban.id}
									sx={{
										...(card?.kanbanId === kanban.id && {
											fontWeight: 'bold',
											backgroundColor: theme.palette.action.selected
										})
									}}
								>
									{kanban.title}
									{card?.kanbanId === kanban.id && " - Quadro atual"}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<FormControl
						size="small"
						fullWidth
						variant="outlined"
						disabled={!selectedKanban || loading.columns}
					>
						<InputLabel>Coluna</InputLabel>
						<Select
							value={selectedColumn}
							label="Coluna"
							onChange={(e) => setSelectedColumn(e.target.value)}
						>
							{columns.map((column) => (
								<MenuItem
									key={column.id}
									value={column.id}
									sx={{
										...(columnId === column.id && {
											fontWeight: 'bold',
											backgroundColor: theme.palette.action.selected
										})
									}}
								>
									{column.title}
									{columnId === column.id && " - Coluna atual"}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Box>
			</DialogContent>
			<DialogActions sx={{
				borderTop: `1px solid ${theme.palette.divider}`,
				padding: '10px'
			}}>
				<Button
					size="small"
					onClick={() => onClose(false)}
					color="error"
					variant="outlined"
					disabled={loading.kanbans || loading.columns || loading.transfer}
				>
					Cancelar
				</Button>
				<Button
					size="small"
					onClick={handleConfirm}
					color="primary"
					variant="contained"
					startIcon={<SwapHorizIcon />}
					loading={loading.kanbans || loading.columns || loading.transfer}
					disabled={!selectedKanban || !selectedColumn || loading.kanbans || loading.columns}
				>
					{'Transferir'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default KanbanTransferCardModal;